// src/utility/userRole.ts
export const MANAGER_BIT = 1 << 0;     // 1 (bit 0)
export const SHOPFLOOR_BIT = 1 << 1;   // 2 (bit 1)

/** The two bits that mean "this account has access to the shop-floor application". */
export const ACCESS_BITS = MANAGER_BIT | SHOPFLOOR_BIT;

function complexityOf(user: any): number {
  // Prefer nested info, fallback to root (defensive)
  const complexityRaw =
    user?.info?.complexity ??
    user?.complexity ??
    0;

  return Number(complexityRaw) || 0;
}

export function userIsManager(user: any): boolean {
  return (complexityOf(user) & MANAGER_BIT) === MANAGER_BIT;
}

export function userIsShopfloor(user: any): boolean {
  return (complexityOf(user) & SHOPFLOOR_BIT) === SHOPFLOOR_BIT;
}

/**
 * Does this user hold access to the application — the Manager box, the Shop Floor box, or both?
 *
 * ⚠️ THIS IS THE RULE THE SITE'S SEAT CAP IS COUNTED BY, AND IT IS MIRRORED ACROSS TWO REPOS.
 * The authority is `Licensing/SeatCapPolicy.cs` in the Function App, which is what actually
 * refuses a save (409 `USER_ACCESS_CAP_EXCEEDED`). This copy exists ONLY so the Users page can
 * warn a manager a moment earlier — it never decides anything on its own, and the page shows
 * the server's own `used` figure rather than deriving one from this.
 *
 * ⚠️ TEST THE BITS, NEVER EQUALITY. `complexity` (dbo.Users.CPNumber) doubles as a legacy
 * skill/complexity rating, so a stored value above 3 is legitimate and can still carry bit 0
 * or bit 1.
 */
export function userHasAccess(user: any): boolean {
  return (complexityOf(user) & ACCESS_BITS) !== 0;
}

/**
 * An **Analytics-only** user: signed in, active, and holding NEITHER access bit.
 *
 * These accounts are deliberately unlimited — they do not consume one of the site's seats.
 * What they can do is sign in, land on Analytics, and open the saved reports that have been
 * **shared with them** (the BFF's `GET api/report/saved` already returns a caller's own reports
 * plus their shares, and `ReportPage` already renders a viewer with no builder, no Save and no
 * Share). Everything else in the app is closed to them — see `AnalyticsOnlyRoute` in `App.tsx`.
 *
 * ⚠️ A SESSION WITH NO USER AT ALL IS NOT ANALYTICS-ONLY. `complexityOf` answers 0 for an
 * unloaded store, a failed licensing call and a genuine viewer alike, so this deliberately
 * requires a record to be present. Without that check, every screen would redirect to
 * /Analytics for the frame or two before `PrivateRoute` has the user — and a licensing failure
 * would land on Analytics instead of the Access Denied screen.
 */
export function userIsAnalyticsOnly(user: any): boolean {
  if (!hasUserRecord(user)) return false;
  return !userHasAccess(user);
}

/**
 * Has the store actually been filled in with a signed-in user yet?
 *
 * `whoto` is the field to key on: the BFF stamps it on every record it returns (`UserInfo`), it
 * is never blank for a real account, and it is what every other role predicate here reads
 * indirectly. `complexity` cannot serve — 0 is a legitimate value for a real user, which is the
 * whole point of the Analytics-only role.
 */
export function hasUserRecord(user: any): boolean {
  const whoto = user?.info?.whoto ?? user?.whoto;
  return typeof whoto === "string" && whoto.trim().length > 0;
}

// Corporate users (whoto CORP / CORP2) may access org-wide routes such as
// /choose-organization. Mirrors the check the CorpRoute guard uses.
export function userIsCorp(user: any): boolean {
  const whoto = user?.info?.whoto;
  return whoto === "CORP" || whoto === "CORP2";
}
